import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Award, Plus, Pencil, Trash2, Loader2 } from "lucide-react";
import {
  getAll,
  create,
  update,
  remove,
} from "../../services/firestoreService";
import { updateCollectionVersion } from "../../services/versionService";
import { SEED_CERTIFICATES } from "../../data/seed";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import ImagePicker from "./ImagePicker";
import SeedDataButton from "./SeedDataButton";

const COLLECTION = "certificates";
const EMPTY_FORM = { title: "", issuer: "", date: "", imageRef: null };

export default function CertificatesEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAll(COLLECTION);
      setItems(data);
    } catch {
      toast.error("Gagal memuat sertifikat");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title || "",
      issuer: item.issuer || "",
      date: item.date || "",
      imageRef: item.imageRef || null,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error("Judul wajib diisi");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await update(COLLECTION, editing.id, form);
        toast.success("Sertifikat berhasil diperbarui!");
      } else {
        await create(COLLECTION, form);
        toast.success("Sertifikat berhasil dibuat!");
      }
      await updateCollectionVersion(COLLECTION);
      setDialogOpen(false);
      loadItems();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove(COLLECTION, deleteTarget.id);
      toast.success("Sertifikat berhasil dihapus!");
      await updateCollectionVersion(COLLECTION);
      setDeleteTarget(null);
      loadItems();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-1 h-6 bg-[#d4af37] rounded-full" />
            <h1 className="text-2xl font-display text-[#fdfbf7]">
              Certificates
            </h1>
          </div>
          <p className="text-sm text-[#a6a196] ml-4">
            Kelola sertifikat & penghargaan
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SeedDataButton
            collectionName={COLLECTION}
            data={SEED_CERTIFICATES}
            onComplete={loadItems}
            isEmpty={items.length === 0}
          />
          <Button
            onClick={openCreate}
            className="bg-[#d4af37] text-[#0a0908] hover:bg-[#d4af37]/90"
          >
            <Plus className="w-4 h-4 mr-1" /> Tambah
          </Button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#d4af37] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-[#a6a196] border border-dashed border-[#d4af37]/20 rounded-lg">
          <Award className="w-12 h-12 mb-3 opacity-20" />
          <p className="mb-4 text-sm">Belum ada sertifikat</p>
          <Button
            onClick={openCreate}
            variant="outline"
            className="border-[#d4af37]/30 text-[#d4af37]"
          >
            <Plus className="w-4 h-4 mr-1" /> Tambah Sertifikat
          </Button>
        </div>
      ) : (
        <>
          {/* Mobile: Card view */}
          <div className="block space-y-3 md:hidden">
            {items.map((item) => (
              <div
                key={item.id}
                className="bg-[#15120f] border border-[#d4af37]/10 rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded overflow-hidden border border-[#d4af37]/10 bg-[#15120f] shrink-0">
                        {item.imageRef ? (
                          <img
                            src={item.imageRef?.url || item.imageRef}
                            alt=""
                            className="object-cover w-full h-full"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#a6a196]">
                            <Award size={14} />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-[#fdfbf7] font-medium truncate">
                          {item.title}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-[#a6a196]">
                          <span className="truncate">{item.issuer}</span>
                          {item.date && <span>· {item.date}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEdit(item)}
                      className="h-8 w-8 text-[#a6a196] hover:text-[#d4af37] hover:bg-[#d4af37]/10"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteTarget(item)}
                      className="h-8 w-8 text-[#a6a196] hover:text-red-400 hover:bg-red-400/10"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Table view */}
          <div className="hidden md:block rounded-lg border border-[#d4af37]/10 overflow-hidden bg-[#0a0908]">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#d4af37]/10">
                    <th className="text-left text-xs text-[#a6a196] uppercase tracking-wider px-4 py-3 font-medium">
                      Image
                    </th>
                    <th className="text-left text-xs text-[#a6a196] uppercase tracking-wider px-4 py-3 font-medium">
                      Title
                    </th>
                    <th className="text-left text-xs text-[#a6a196] uppercase tracking-wider px-4 py-3 font-medium">
                      Issuer
                    </th>
                    <th className="text-left text-xs text-[#a6a196] uppercase tracking-wider px-4 py-3 font-medium">
                      Date
                    </th>
                    <th className="text-right text-xs text-[#a6a196] uppercase tracking-wider px-4 py-3 font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#d4af37]/5">
                  {items.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-[#15120f]/50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        {item.imageRef ? (
                          <img
                            src={item.imageRef?.url || item.imageRef}
                            alt=""
                            className="w-12 h-12 object-cover rounded border border-[#d4af37]/10"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded bg-[#15120f] flex items-center justify-center text-[#a6a196]">
                            <Award size={14} />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-[#fdfbf7] font-medium">
                          {item.title}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-[#a6a196]">
                          {item.issuer}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-[#a6a196]">
                          {item.date}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEdit(item)}
                            className="h-8 w-8 text-[#a6a196] hover:text-[#d4af37] hover:bg-[#d4af37]/10"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteTarget(item)}
                            className="h-8 w-8 text-[#a6a196] hover:text-red-400 hover:bg-red-400/10"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#15120f] border-[#d4af37]/20 text-[#fdfbf7] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#fdfbf7]">
              {editing ? "Edit Sertifikat" : "Tambah Sertifikat"}
            </DialogTitle>
            <DialogDescription className="text-[#a6a196]">
              {editing
                ? "Ubah detail sertifikat"
                : "Isi detail sertifikat baru"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-[#a6a196]">Judul</label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Nama sertifikat"
                className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-[#a6a196]">Penerbit</label>
                <Input
                  value={form.issuer}
                  onChange={(e) => setForm({ ...form, issuer: e.target.value })}
                  placeholder="Penerbit"
                  className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-[#a6a196]">Tanggal</label>
                <Input
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  placeholder="2024"
                  className="bg-[#0a0908] border-[#d4af37]/20 text-[#fdfbf7] placeholder:text-[#a6a196]/50"
                />
              </div>
            </div>
            <ImagePicker
              value={form.imageRef}
              onChange={(img) => setForm({ ...form, imageRef: img })}
              label="Certificate Image"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className="border-[#d4af37]/20 text-[#a6a196]"
            >
              Batal
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#d4af37] text-[#0a0908] hover:bg-[#d4af37]/90"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" /> Menyimpan...
                </>
              ) : editing ? (
                "Perbarui"
              ) : (
                "Buat"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
      >
        <AlertDialogContent className="bg-[#15120f] border-[#d4af37]/20 text-[#fdfbf7]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#fdfbf7]">
              Hapus Sertifikat
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#a6a196]">
              Apakah Anda yakin ingin menghapus "{deleteTarget?.title}"?
              Tindakan ini tidak dapat dibatalkan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#0a0908] border-[#d4af37]/20 text-[#a6a196] hover:bg-[#15120f]">
              Batal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="text-red-400 border bg-red-500/10 border-red-500/20 hover:bg-red-500/20"
            >
              Hapus
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
